import { codeFolding, foldEffect, foldedRanges, unfoldEffect } from "@codemirror/language";
import { StateField, type EditorState, type Extension, type Transaction } from "@codemirror/state";
import { Decoration, type DecorationSet, EditorView, WidgetType } from "@codemirror/view";

/**
 * The preamble folded (the user, 11 Oct: "preamble should be expandable"): a
 * full document's View LaTeX opens on its body, the lines before
 * \begin{document} shown as one bar, "Preamble · 24 lines", that opens on a tap.
 * Open, a "▾ Hide preamble" bar above it folds it again (the user, 11 Oct: once
 * opened, the bar disappeared and nothing folded it back).
 */

/** Lines 1 to the one before \begin{document}, when that is two lines or more. */
export function preambleRange(state: EditorState): { from: number; to: number; lines: number } | null {
  const doc = state.doc;
  for (let n = 1; n <= doc.lines; n++) {
    if (/^\s*\\begin\{document\}/.test(doc.line(n).text)) {
      // The blank lines before \begin{document} are the gap (latex-rhythm), not the preamble.
      let last = n - 1;
      while (last >= 1 && !doc.line(last).text.trim()) last--;
      if (last < 2) return null;
      return { from: doc.line(1).from, to: doc.line(last).to, lines: last };
    }
  }
  return null;
}

export const PREAMBLE_FOLD_CLASS = "cm-preamble-fold";

function isFolded(state: EditorState, r: { from: number; to: number }): boolean {
  let folded = false;
  foldedRanges(state).between(r.from, r.to, (from, to) => {
    if (from === r.from && to === r.to) folded = true;
  });
  return folded;
}

/** "▾ Hide preamble · 24 lines", above an open preamble. */
class HideBar extends WidgetType {
  constructor(readonly lines: number) {
    super();
  }
  eq(other: HideBar) {
    return other.lines === this.lines;
  }
  toDOM(view: EditorView) {
    const wrap = document.createElement("div");
    wrap.className = "cm-preamble-hide-wrap";
    const bar = document.createElement("button");
    bar.type = "button";
    bar.className = PREAMBLE_FOLD_CLASS;
    bar.setAttribute("data-testid", "latex-preamble-hide");
    bar.textContent = `▾ Hide preamble · ${this.lines} lines`;
    bar.onclick = () => view.dispatch({ effects: foldPreambleEffects(view.state) });
    wrap.append(bar);
    return wrap;
  }
  ignoreEvent() {
    return false;
  }
}

function hideBarDecorations(state: EditorState): DecorationSet {
  const r = preambleRange(state);
  if (!r || isFolded(state, r)) return Decoration.none;
  return Decoration.set([Decoration.widget({ widget: new HideBar(r.lines), block: true, side: -1 }).range(r.from)]);
}

// A block widget has to come from state, not a view plugin (CodeMirror refuses it there).
const hideBar = StateField.define<DecorationSet>({
  create: hideBarDecorations,
  update(deco, tr) {
    return tr.docChanged || tr.effects.length || tr.reconfigured ? hideBarDecorations(tr.state) : deco;
  },
  provide: (f) => EditorView.decorations.from(f),
});

/** Folding with the preamble's own bar as the placeholder. */
export function preambleFolding(): Extension {
  return [
    codeFolding({
      preparePlaceholder: (state, range) => state.doc.lineAt(range.to).number - state.doc.lineAt(range.from).number + 1,
      placeholderDOM: (_view, onclick, lines) => {
        const bar = document.createElement("button");
        bar.type = "button";
        bar.className = PREAMBLE_FOLD_CLASS;
        bar.setAttribute("data-testid", "latex-preamble-fold");
        bar.setAttribute("aria-label", `Show the preamble, ${lines} lines`);
        bar.textContent = `▸ Preamble · ${lines} lines`;
        bar.onclick = onclick;
        return bar;
      },
    }),
    hideBar,
    EditorView.baseTheme({
      ".cm-preamble-hide-wrap": { padding: "0" },
      // Olivia, 11 Oct: a full-width bar on the muted grey, 32 px (44 on a phone).
      [`.${PREAMBLE_FOLD_CLASS}`]: {
        // Inline: a block-level box inside the line would add an empty row above and below it.
        display: "inline-flex",
        verticalAlign: "middle",
        boxSizing: "border-box",
        alignItems: "center",
        width: "100%",
        minHeight: "var(--preamble-bar-h, 32px)",
        margin: "0",
        padding: "0 12px",
        border: "0",
        borderBottom: "1px solid var(--lilia-border, #d3d9e1)",
        borderRadius: "0",
        background: "var(--lilia-bg-muted, #eef1f5)",
        color: "var(--lilia-text-secondary, #3b4757)",
        fontFamily: "var(--lilia-font-sans, Inter, system-ui, sans-serif)",
        fontSize: "12px",
        fontWeight: "600",
        textAlign: "left",
        cursor: "pointer",
      },
    }),
  ];
}

/** Fold the preamble now, if there is one. */
export function foldPreambleEffects(state: EditorState) {
  const r = preambleRange(state);
  return r ? [foldEffect.of({ from: r.from, to: r.to })] : [];
}

/** True once the reader opened the preamble (an unfold), so a refresh leaves it open. */
export function isUnfold(tr: Transaction): boolean {
  return tr.effects.some((e) => e.is(unfoldEffect));
}
